import { DecimalField, useForm, useFormState } from '@ff-it/form';
import { useEffect } from 'react';
import type { Activity, ActivityType } from 'types';
import { FactorPolicyField } from '../factorPolicy/FactorPolicyField';
import { clearRuleValues, ruleValueFields, type RuleValueSide } from './form';
import { discountContextLabels } from './presentation';
import * as styles from './DiscountRuleSides.css';

export function DiscountRuleValues({
  activeType,
  sides,
  productKind = 'MEDIA',
  hideIncompatible = false,
}: {
  activeType: ActivityType;
  sides: readonly RuleValueSide[];
  productKind?: Activity['kind'];
  hideIncompatible?: boolean;
}) {
  const form = useForm();
  const { values } = useFormState<Record<string, unknown> & { target_activity: Activity | null }>({
    subscription: { values: true },
  });
  const fields = ruleValueFields(activeType, values.target_activity, sides, productKind);
  useEffect(() => {
    const cleared = clearRuleValues(values, fields);
    form.batch(() => {
      for (const { name } of fields) {
        if (values[name] !== cleared[name]) form.change(name, cleared[name]);
      }
    });
  }, [fields, form, values]);

  return (
    <div className={sides.length === 1 ? styles.singleSide : styles.sides}>
      {sides.map((side) => {
        const label = side.side === 'client' ? 'Client' : 'Provider';
        return (
          <section
            key={side.side}
            className={`${styles.side} ${side.side === 'client' ? styles.clientTone : styles.providerTone}`}
            aria-label={`${label} discounts`}
          >
            <h3 className={styles.heading}>{label}</h3>
            <div className={styles.fields}>
              {fields
                .filter((field) => field.side === side)
                .map((field) => {
                  if (!field.context)
                    return activeType === 'RADIO' && productKind === 'MEDIA' ? (
                      <FactorPolicyField
                        key={field.name}
                        name={side.policyField}
                        side={label}
                        disabled={!field.applicable}
                      />
                    ) : null;
                  const populated = fields.some(
                    (other) => other.context === field.context && values[other.name] != null,
                  );
                  if (
                    hideIncompatible
                      ? !field.applicable && !populated
                      : field.context === 'package_discount' && activeType !== 'RADIO' && !populated
                  )
                    return null;
                  const fieldLabel =
                    field.context === 'standard_discount' && productKind === 'SERVICE'
                      ? 'Service'
                      : discountContextLabels[field.context];
                  return (
                    <DecimalField
                      key={field.name}
                      className="mb-0"
                      name={field.name}
                      label={fieldLabel}
                      aria-label={sides.length === 2 ? `${fieldLabel} ${side.side}` : undefined}
                      size="sm"
                      precision={2}
                      disabled={!field.applicable}
                      help={!field.applicable ? 'Not applicable.' : undefined}
                    />
                  );
                })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
